import { useState, useCallback, useEffect } from 'react';

const ScriptStatus = {
  Idle: 'idle',
  Loading: 'loading',
  Ready: 'ready',
  Error: 'error'
};
function isScriptStatus(status) {
  return typeof status === 'string' && Object.values(ScriptStatus).includes(status);
}
const isBrowser = typeof window !== 'undefined';
const cachedScriptStatuses = isBrowser ? new Map() : null;
function getCachedStatus(src) {
  return cachedScriptStatuses?.get(src);
}
function setCachedStatus(src, status) {
  cachedScriptStatuses?.set(src, status);
}
function getScriptNode(src) {
  const node = document.querySelector(`script[src="${src}"]`);
  const statusAttr = node?.getAttribute('data-status');
  const status = isScriptStatus(statusAttr) ? statusAttr : ScriptStatus.Loading;
  return {
    node,
    status
  };
}
function useScript(src) {
  let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const {
    shouldPreventLoad = false,
    removeOnUnmount = false,
    id,
    timeout = NaN,
    loadingAttribute = null
  } = options;
  const [scriptState, setScriptState] = useState(() => ({
    status: !src || shouldPreventLoad ? ScriptStatus.Idle : !isBrowser ? ScriptStatus.Loading : getCachedStatus(src) || ScriptStatus.Loading,
    error: null
  }));
  const setStatus = useCallback(function (status) {
    let error = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    if (!isScriptStatus(status)) {
      // eslint-disable-next-line no-console
      console.warn(`Invalid script status: ${status}`);
      return;
    }
    setScriptState({
      status,
      error
    });
    if (src) {
      setCachedStatus(src, status);
    }
  }, [src]);
  useEffect(() => {
    if (!src || shouldPreventLoad) {
      return;
    }
    const cachedStatus = getCachedStatus(src);
    if (cachedStatus === ScriptStatus.Ready || cachedStatus === ScriptStatus.Error) {
      setStatus(cachedStatus);
      return;
    }
    const {
      node: existingNode
    } = getScriptNode(src);
    let scriptElement;
    if (!existingNode) {
      scriptElement = document.createElement('script');
      scriptElement.src = src;
      if (id) {
        scriptElement.id = id;
      }
      scriptElement.setAttribute('data-status', ScriptStatus.Loading);
      switch (loadingAttribute) {
        case 'async':
          scriptElement.async = true;
          break;
        case 'defer':
          scriptElement.defer = true;
          break;
        case 'async-defer':
          scriptElement.async = true;
          scriptElement.defer = true;
          break;
      }
      document.body.appendChild(scriptElement);
    } else {
      scriptElement = existingNode;
    }
    const handleScriptLoad = () => {
      setStatus(ScriptStatus.Ready);
      scriptElement.setAttribute('data-status', ScriptStatus.Ready);
    };
    const handleScriptError = error => {
      const errorMessage = error instanceof ErrorEvent ? error.error : new Error('Unknown script loading error');
      setStatus(ScriptStatus.Error, errorMessage);
      scriptElement.setAttribute('data-status', ScriptStatus.Error);
    };
    scriptElement.addEventListener('load', handleScriptLoad);
    scriptElement.addEventListener('error', handleScriptError);
    const removeDynamicScriptElement = () => {
      if (!existingNode && removeOnUnmount) {
        scriptElement.remove();
        setCachedStatus(src, ScriptStatus.Idle);
      }
    };
    const timeoutId = !isNaN(timeout) ? setTimeout(() => {
      handleScriptError(new ErrorEvent('error', {
        error: new Error(`Script load timeout for src: ${src}`)
      }));
    }, timeout) : null;
    return () => {
      scriptElement.removeEventListener('load', handleScriptLoad);
      scriptElement.removeEventListener('error', handleScriptError);
      removeDynamicScriptElement();
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [src, shouldPreventLoad, removeOnUnmount, id, timeout, loadingAttribute, setStatus]);
  return scriptState;
}
function cleanScriptCache() {
  cachedScriptStatuses?.clear();
}

export { ScriptStatus, cleanScriptCache, useScript as default };
